import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp, deleteField, arrayUnion, arrayRemove, writeBatch, collection } from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';

// Re-exporta utilitarios Firestore para desacoplar componentes do firebase/firestore
export { serverTimestamp, deleteField, arrayUnion, arrayRemove };

export function createWorkspaceDocRef(appId, workspaceKey) {
  return doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', workspaceKey);
}

export async function fetchWorkspaceDoc(workspaceDocRef) {
  const snap = await getDoc(workspaceDocRef);
  return { exists: snap.exists(), data: snap.exists() ? snap.data() || {} : {} };
}

export async function saveWorkspaceDoc(workspaceDocRef, data) {
  await setDoc(workspaceDocRef, data, { merge: true });
}

/**
 * ITENS B+D da estratégia definitiva do Motor de Anexos (26/08/2026): grava o
 * documento do workspace E um retrato/histórico daquele salvamento, os dois
 * juntos, atômico (writeBatch) — ou os dois gravam, ou nenhum grava.
 *
 * Por que writeBatch aqui e não em saveWorkspaceDoc: um único documento já é
 * atômico por padrão no Firestore (nunca fica "pela metade"). O writeBatch só
 * passa a valer a pena quando existem DOIS documentos envolvidos — é
 * exatamente este caso: o documento principal do workspace + um novo
 * documento na subcoleção de histórico.
 *
 * @param {DocumentReference} workspaceDocRef
 * @param {object} data - mesmo payload que saveWorkspaceDoc(merge:true) recebia
 * @param {object|null} historySnapshot - se fornecido, também grava um retrato
 *   imutável em autos_workspaces/{id}/anexos_history/{autoId}. Passar null
 *   para gravar só o documento principal (equivalente a saveWorkspaceDoc, mas
 *   ainda assim dentro do batch).
 */
export async function saveWorkspaceDocWithHistory(workspaceDocRef, data, historySnapshot) {
  const batch = writeBatch(db);
  batch.set(workspaceDocRef, data, { merge: true });
  if (historySnapshot) {
    const historyColRef = collection(workspaceDocRef, 'anexos_history');
    const historyDocRef = doc(historyColRef);
    batch.set(historyDocRef, { ...historySnapshot, savedAt: serverTimestamp() });
  }
  await batch.commit();
}

export async function patchWorkspaceDoc(workspaceDocRef, data) {
  await updateDoc(workspaceDocRef, { ...data, updatedAt: serverTimestamp() });
}

// FIX A (03/09/2026, RELATORIO_DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA):
// Camada 1 do loop infinito ao trocar de peça — esta chamada nunca filtrava
// eco da própria escrita. Toda escrita otimista do PRÓPRIO cliente num doc
// com listener ativo já dispara o callback antes de ir pro servidor
// (hasPendingWrites: true), indistinguível de uma mudança feita por fora.
// `includeMetadataChanges: true` + ignorar hasPendingWrites: true entrega só
// o snapshot já confirmado pelo servidor — filtro nativo do Firestore, no
// lugar da comparação caseira de timestamp que só cobria 1 dos 4 campos
// irmãos (ver Camada 2 do mesmo relatório).
export function subscribeWorkspaceDoc(workspaceDocRef, onSnap, onError) {
  return onSnapshot(
    workspaceDocRef,
    { includeMetadataChanges: true },
    (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      onSnap(snap);
    },
    onError,
  );
}

/** Helper: remove um campo (deleteField) de um workspace. */
export async function removeWorkspaceField(workspaceDocRef, fieldPath) {
  await updateDoc(workspaceDocRef, { [fieldPath]: deleteField(), updatedAt: serverTimestamp() });
}

/** Helper: adiciona item a um array (arrayUnion) de um workspace. */
export async function appendToWorkspaceArray(workspaceDocRef, fieldPath, value) {
  await updateDoc(workspaceDocRef, { [fieldPath]: arrayUnion(value), updatedAt: serverTimestamp() });
}
